import DOMPurify from 'dompurify';

export interface SanitizationOptions {
  allowScripts?: boolean;
  allowIframes?: boolean;
  sanitizationLevel?: 'strict' | 'moderate' | 'minimal';
}

export const sanitizeHtml = (html: string, options: SanitizationOptions = {}): string => {
  // Ensure options is never null/undefined
  const safeOptions = options || {};
  const { sanitizationLevel = 'strict' } = safeOptions;

  // Add hook to filter problematic styles only in strict mode
  if (sanitizationLevel === 'strict') {
    DOMPurify.addHook('afterSanitizeAttributes', (node) => {
      // Remove inline styles that might override text colors
      if (node.hasAttribute('style')) {
        const style = node.getAttribute('style') || '';
        // Remove color-related styles but keep others
        const filteredStyle = style
          .split(';')
          .filter(rule => {
            const trimmed = rule.trim().toLowerCase();
            // Remove color, background-color, and similar properties
            return !trimmed.startsWith('color:') && 
                   !trimmed.startsWith('background-color:') &&
                   !trimmed.startsWith('background:');
          })
          .join(';');
        
        if (filteredStyle.trim()) {
          node.setAttribute('style', filteredStyle);
        } else {
          node.removeAttribute('style');
        }
      }
    });
  }

  let config: Record<string, any> = {};

  switch (sanitizationLevel) {
    case 'minimal':
      // Allow most HTML but no scripts
      config = {
        ALLOWED_TAGS: undefined, // Allow all tags
        ALLOWED_ATTR: undefined, // Allow all attributes
        FORBID_TAGS: ['script', 'object', 'embed'],
        FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
      };
      break;

    case 'moderate':
      // Allow common formatting and structure
      config = {
        ALLOWED_TAGS: [
          'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
          'p', 'div', 'span', 'br', 'hr',
          'a', 'img', 'blockquote', 'pre', 'code',
          'ul', 'ol', 'li', 'dl', 'dt', 'dd',
          'table', 'thead', 'tbody', 'tr', 'th', 'td',
          'strong', 'em', 'b', 'i', 'u', 's', 'del', 'ins',
          'sup', 'sub', 'mark', 'small',
          'details', 'summary', 'figure', 'figcaption',
        ],
        ALLOWED_ATTR: [
          'href', 'src', 'alt', 'title', 'width', 'height',
          'class', 'id', 'style', 'target', 'rel',
          'colspan', 'rowspan', 'headers', 'scope',
          'data-*', 'aria-*', 'role',
        ],
        ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|blob|data):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
      };
      break;

    case 'strict':
    default:
      // Very limited HTML for safety
      config = {
        ALLOWED_TAGS: [
          'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
          'p', 'div', 'span', 'br',
          'a', 'blockquote', 'pre', 'code',
          'ul', 'ol', 'li',
          'table', 'thead', 'tbody', 'tr', 'th', 'td',
          'strong', 'em', 'b', 'i', 'u',
        ],
        ALLOWED_ATTR: ['href', 'title', 'class'],
        ALLOWED_URI_REGEXP: /^https?:|^mailto:/i,
      };
      break;
  }

  // Additional configurations based on options
  if (options.allowIframes && sanitizationLevel !== 'strict') {
    config.ALLOWED_TAGS = [...(config.ALLOWED_TAGS || []), 'iframe'];
    config.ALLOWED_ATTR = [...(config.ALLOWED_ATTR || []), 'src', 'frameborder', 'allowfullscreen'];
    config.ADD_ATTR = ['target'];
  }

  // Always forbid scripts unless explicitly allowed (which we never do)
  // Only forbid style tag in strict mode
  if (sanitizationLevel === 'strict') {
    config.FORBID_TAGS = [...(config.FORBID_TAGS || []), 'script', 'style'];
  } else {
    config.FORBID_TAGS = [...(config.FORBID_TAGS || []), 'script'];
  }
  config.FORBID_ATTR = [...(config.FORBID_ATTR || []), 'onerror', 'onload', 'onclick'];

  // Sanitize data URIs to prevent XSS
  config.ALLOW_DATA_ATTR = false;
  config.ALLOW_UNKNOWN_PROTOCOLS = false;

  // Remove HTML comments that might contain directives
  const cleanHtml = html.replace(/<!--HTML-->/g, '');

  // Sanitize first
  const sanitized = DOMPurify.sanitize(cleanHtml, { ...config, RETURN_DOM_FRAGMENT: false, RETURN_DOM: false }) as unknown as string;

  // Remove the hook after use to prevent memory leaks (only if we added it)
  if (sanitizationLevel === 'strict') {
    DOMPurify.removeHook('afterSanitizeAttributes');
  }

  // Then ensure contrast
  return ensureContrast(sanitized);
};

// Helper to extract text content from HTML
export const extractTextFromHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

// Helper to check if content is HTML
export const isHtmlContent = (content: string): boolean => {
  return content.trim().startsWith('<!--HTML-->') || /<[^>]+>/.test(content);
};



// Post-process HTML to ensure good contrast
export const ensureContrast = (html: string): string => {
  // Since we're already removing color styles in the sanitizer hook,
  // and setting proper colors in the CSS, we don't need to do additional
  // color processing here. Just return the HTML as-is.
  return html;
};